/**
 * URL parsing utilities for the auth feature.
 * These utilities handle query parameter extraction and encoding.
 */

/**
 * Parses query parameters from a URL string or the current location.
 * @param search - The query string to parse (defaults to window.location.search)
 * @returns URLSearchParams instance
 */
export const parseQueryParams = (search?: string): URLSearchParams => {
    const searchString =
        search ?? (typeof window !== 'undefined' ? window.location.search : '');
    return new URLSearchParams(searchString);
};

/**
 * Gets a specific query parameter value.
 * @param paramName - The parameter name
 * @param search - Optional query string (defaults to window.location.search)
 * @returns The parameter value or null if not found
 */
export const getQueryParam = (
    paramName: string,
    search?: string,
): string | null => {
    const params = parseQueryParams(search);
    return params.get(paramName);
};

/**
 * Gets a numeric query parameter value.
 * @param paramName - The parameter name
 * @param search - Optional query string (defaults to window.location.search)
 * @returns The parsed number or null if not found/invalid
 */
export const getNumericQueryParam = (
    paramName: string,
    search?: string,
): number | null => {
    const value = getQueryParam(paramName, search);
    if (value === null) {
        return null;
    }

    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
};

/**
 * Gets a boolean query parameter value.
 * @param paramName - The parameter name
 * @param search - Optional query string (defaults to window.location.search)
 * @returns The parsed boolean or null if not found
 */
export const getBooleanQueryParam = (
    paramName: string,
    search?: string,
): boolean | null => {
    const value = getQueryParam(paramName, search);
    if (value === null) {
        return null;
    }

    const lowerValue = value.toLowerCase();
    if (lowerValue === 'true' || lowerValue === '1') {
        return true;
    }
    if (lowerValue === 'false' || lowerValue === '0') {
        return false;
    }
    return null;
};

/**
 * Builds a URL with query parameters.
 * @param baseUrl - The base URL
 * @param params - Query parameters to add
 * @returns The complete URL with query string
 */
export const buildUrlWithParams = (
    baseUrl: string,
    params: Record<string, string | number | boolean>,
): string => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.set(key, String(value));
        }
    });

    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Encodes a string for use in a URL.
 * @param value - The string to encode
 * @returns URL-encoded string
 */
export const encodeUrlParam = (value: string): string =>
    encodeURIComponent(value);
